import { useCallback } from 'react';
import { useCutList, ALGORITHMS, SHAPE_TYPES } from '../contexts/CutListContext';
import { formatDimension, formatLayoutLabel, UNIT_SYSTEMS, getThicknessTolerance } from '../utils/units';
import { getPiecePolygon, nestPolygons } from '../utils/polygonNesting';
import { AnyNest, FloatPolygon } from 'any-nest';

// Import extracted pure algorithm functions
import { sortByDescendingShortSide, getPossibleOrientations, canPlaceAt, findBestPosition } from '../algorithms/common.js';
import { tryOptimizeWithSheets } from '../algorithms/bottomLeft.js';
import { guillotineOptimize } from '../algorithms/guillotine.js';
import { maximalRectanglesOptimize } from '../algorithms/maximalRectangles.js';
import { skylineOptimize } from '../algorithms/skyline.js';
import { ffdhOptimize } from '../algorithms/ffdh.js';
import { bestResultOptimize, bestResultOptimizeAsync } from '../algorithms/bestResult.js';

// Boundary mapping: app convention (width=y, length=x) ↔ algorithm convention (width=x, height=y)
const toAlgoSpace = (piece) => ({ ...piece, width: piece.length, height: piece.width });
const fromAlgoSpace = (piece) => ({ ...piece, length: piece.width, width: piece.height });

/**
 * Validate optimization result - check for impossible layouts
 * Returns true if valid, false if invalid (e.g., overlapping pieces)
 */
const validateOptimizationResult = (result, sheetWidth, sheetHeight) => {
  if (!result || !result.layouts) return false;

  const sheetArea = sheetWidth * sheetHeight;

  for (const layout of result.layouts) {
    // Calculate used area on this sheet
    const usedArea = layout.pieces.reduce((sum, piece) => {
      return sum + (piece.width * piece.height);
    }, 0);

    // If used area exceeds sheet area, pieces must be overlapping
    const efficiency = usedArea / sheetArea;
    if (efficiency > 0.98) { // Allow tiny float error, but 98%+ is suspicious
      console.warn(`Validation: Sheet ${layout.id} has ${(efficiency * 100).toFixed(1)}% area usage - likely overlapping pieces`);
      return false;
    }
  }

  return true;
};

/**
 * Fallback optimization using rectangle bounding boxes
 * Treats all pieces as their bounding rectangles for reliable packing
 */
const rectangleFallbackOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, algorithmType) => {
  console.log('Fallback: Using rectangle bounding boxes for all pieces');

  // Convert all pieces to rectangles (strip polygon data)
  const rectangularPieces = allPieces.map(piece => ({
    ...piece,
    shapeType: 'rectangle',
    vertices: null // Force rectangle treatment
  }));

  // Use the selected algorithm
  switch (algorithmType) {
    case ALGORITHMS.GUILLOTINE:
      return guillotineOptimize(rectangularPieces, sheetWidth, sheetHeight, cutWidth);

    case ALGORITHMS.MAXIMAL_RECTANGLES:
      return maximalRectanglesOptimize(rectangularPieces, sheetWidth, sheetHeight, cutWidth);

    case ALGORITHMS.BOTTOM_LEFT:
    default:
      // Use the iterative approach for bottom-left
      const totalPieceArea = rectangularPieces.reduce((sum, piece) =>
        sum + (piece.width * piece.height), 0);
      const sheetArea = sheetWidth * sheetHeight;
      const theoreticalMin = Math.ceil(totalPieceArea / sheetArea);

      let sheetsToTry = Math.max(theoreticalMin, 1);
      const maxSheets = rectangularPieces.length;
      let bestResult = null;

      while (sheetsToTry <= maxSheets) {
        const result = tryOptimizeWithSheets(rectangularPieces, sheetsToTry, sheetWidth, sheetHeight, cutWidth);

        if (result.unplacedCount === 0) {
          return result;
        }

        if (!bestResult || result.unplacedCount < bestResult.unplacedCount) {
          bestResult = result;
        }

        sheetsToTry++;
      }
      return bestResult;
  }
};

/**
 * Check if any pieces in the list are non-rectangular
 */
const hasNonRectangularPieces = (pieces) => {
  return pieces.some(p =>
    p.shapeType && p.shapeType !== 'rectangle' && p.shapeType !== SHAPE_TYPES.RECTANGLE
  );
};

// =============================================================================
// SVGNEST ALGORITHM (using any-nest library)
// =============================================================================

/**
 * Convert our piece format to FloatPolygon for any-nest
 */
const pieceToFloatPolygon = (piece, index) => {
  let points;

  if (piece.vertices && piece.vertices.length >= 3) {
    // Use actual polygon vertices
    points = piece.vertices.map(v => ({ x: v.x, y: v.y }));
  } else {
    // Create rectangle vertices
    points = [
      { x: 0, y: 0 },
      { x: piece.width, y: 0 },
      { x: piece.width, y: piece.height },
      { x: 0, y: piece.height }
    ];
  }

  const id = piece.id ? String(piece.id) : `piece_${index}`;
  return FloatPolygon.fromPoints(points, id);
};

/**
 * SVGnest-based optimization using the any-nest library
 * Uses a genetic algorithm for optimal polygon nesting
 *
 * @param {Array} allPieces - Expanded pieces array
 * @param {number} sheetWidth - Sheet width
 * @param {number} sheetHeight - Sheet height
 * @param {number} cutWidth - Blade kerf
 * @param {Object} options - Additional options (timeLimit, rotations)
 * @returns {Promise<Object>} - { layouts, unplacedPieces, unplacedCount }
 */
const svgnestOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, options = {}) => {
  return new Promise((resolve) => {
    const {
      timeLimit = 8000, // Run for 8 seconds by default
      rotationSteps = 4  // 0, 90, 180, 270 degrees
    } = options;

    console.log(`SVGnest: Starting optimization with ${allPieces.length} pieces`);
    console.log(`SVGnest: Sheet ${sheetWidth}x${sheetHeight}, kerf ${cutWidth}, time limit ${timeLimit}ms`);

    try {
      const nest = new AnyNest();

      // Create bin (sheet) polygon
      const binPoints = [
        { x: 0, y: 0 },
        { x: sheetWidth, y: 0 },
        { x: sheetWidth, y: sheetHeight },
        { x: 0, y: sheetHeight }
      ];
      console.log('SVGnest: Creating bin polygon:', binPoints);
      const binPolygon = FloatPolygon.fromPoints(binPoints, 'bin');
      console.log('SVGnest: Bin polygon created:', binPolygon);
      nest.setBin(binPolygon);

      // Convert pieces to FloatPolygons
      console.log('SVGnest: Converting pieces to FloatPolygons...');
      const partPolygons = [];
      for (let i = 0; i < allPieces.length; i++) {
        try {
          const poly = pieceToFloatPolygon(allPieces[i], i);
          console.log(`SVGnest: Piece ${i} (${allPieces[i].name || 'unnamed'}):`, poly?.points?.length, 'points');
          partPolygons.push(poly);
        } catch (pieceError) {
          console.error(`SVGnest: Error converting piece ${i}:`, pieceError);
        }
      }
      console.log(`SVGnest: Created ${partPolygons.length} part polygons`);
      nest.setParts(partPolygons);

    // Configure the algorithm
    // Determine rotation steps based on grain directions
    let rotations = rotationSteps;
    const hasGrainRestricted = allPieces.some(p =>
      p.grainDirection && p.grainDirection !== 'none'
    );
    if (hasGrainRestricted) {
      rotations = 2; // Only 0 and 180 degrees for grain-restricted pieces
    }

    nest.config({
      spacing: cutWidth,
      binSpacing: cutWidth,
      rotations: rotations,
      populationSize: 10,
      mutationRate: 10,
      useHoles: false,
      exploreConcave: true
    });

    let bestPlacements = null;
    let bestUtilization = 0;
    const startTime = Date.now();

    // Progress callback
    const onProgress = (progress) => {
      // Check time limit
      if (Date.now() - startTime > timeLimit) {
        nest.stop();
      }
    };

    // Display callback - called when a new best solution is found
    const onDisplay = (placements, utilization) => {
      if (placements && utilization > bestUtilization) {
        bestPlacements = placements;
        bestUtilization = utilization;
        console.log(`SVGnest: New best solution - ${utilization.toFixed(1)}% utilization, ${placements.length} bin(s)`);
      }

      // Check time limit
      if (Date.now() - startTime > timeLimit) {
        nest.stop();
      }
    };

    // Start the algorithm
    console.log('SVGnest: Starting genetic algorithm...');
    nest.start(onProgress, onDisplay);

    // Set a timeout to stop and return results
    setTimeout(() => {
      console.log('SVGnest: Timeout reached, stopping...');
      nest.stop();

      // Convert placements back to our layout format
      console.log('SVGnest: Best placements:', bestPlacements);
      const result = convertSvgnestResult(bestPlacements, allPieces, sheetWidth, sheetHeight);
      console.log(`SVGnest: Complete - ${result.layouts.length} sheets, ${result.unplacedCount} unplaced`);

      resolve(result);
    }, timeLimit + 500); // Give a little extra time for cleanup

    } catch (error) {
      console.error('SVGnest: Error during setup:', error);
      resolve({
        layouts: [],
        unplacedPieces: allPieces,
        unplacedCount: allPieces.length
      });
    }
  });
};

/**
 * Convert SVGnest placements back to our layout format
 */
const convertSvgnestResult = (placements, originalPieces, sheetWidth, sheetHeight) => {
  if (!placements || placements.length === 0) {
    console.warn('SVGnest: No valid placements found');
    return {
      layouts: [],
      unplacedPieces: originalPieces,
      unplacedCount: originalPieces.length
    };
  }

  const layouts = [];
  const placedPieceIds = new Set();

  // Each element in placements array is a bin (sheet)
  placements.forEach((binPlacements, binIndex) => {
    const layout = {
      id: binIndex + 1,
      width: sheetWidth,
      height: sheetHeight,
      pieces: []
    };

    binPlacements.forEach(placement => {
      // Find the original piece by ID
      const originalPiece = originalPieces.find(p =>
        String(p.id) === placement.id || `piece_${originalPieces.indexOf(p)}` === placement.id
      );

      if (originalPiece) {
        placedPieceIds.add(originalPiece.id);

        // Get the piece polygon for dimensions
        const piecePolygon = getPiecePolygon(originalPiece);
        const rotated = placement.rotate !== 0;

        // Calculate rotated dimensions if needed
        let width = originalPiece.width;
        let height = originalPiece.height;
        if (rotated && (placement.rotate === 90 || placement.rotate === 270)) {
          width = originalPiece.height;
          height = originalPiece.width;
        }

        layout.pieces.push({
          ...originalPiece,
          x: placement.translate.x,
          y: placement.translate.y,
          width,
          height,
          rotated,
          rotationAngle: placement.rotate,
          vertices: piecePolygon
        });
      }
    });

    if (layout.pieces.length > 0) {
      layouts.push(layout);
    }
  });

  // Find unplaced pieces
  const unplacedPieces = originalPieces.filter(p => !placedPieceIds.has(p.id));

  return {
    layouts,
    unplacedPieces,
    unplacedCount: unplacedPieces.length
  };
};

// =============================================================================
// STATISTICS
// =============================================================================

/**
 * Calculate statistics for the optimization result
 * (Legacy function for uniform sheet sizes)
 */
const calculateStats = (layouts, pieces, sheetWidth, sheetLength) => {
  const totalSheetArea = layouts.length * sheetWidth * sheetLength;
  const usedArea = layouts.reduce((sum, layout) =>
    sum + layout.pieces.reduce((pieceSum, piece) =>
      pieceSum + (piece.width * piece.length), 0), 0);
  const wastedArea = totalSheetArea - usedArea;
  const efficiency = totalSheetArea > 0 ? (usedArea / totalSheetArea * 100) : 0;

  const totalOriginalPieces = pieces.reduce((sum, piece) => sum + piece.quantity, 0);
  const placedCount = layouts.reduce((sum, layout) => sum + layout.pieces.length, 0);

  return {
    totalSheets: layouts.length,
    usedArea: usedArea.toFixed(2),
    wastedArea: wastedArea.toFixed(2),
    efficiency: efficiency.toFixed(1),
    unplacedPieces: totalOriginalPieces - placedCount,
    placedPieces: placedCount,
    totalPieces: totalOriginalPieces
  };
};

/**
 * Calculate statistics for optimization result with per-sheet dimensions
 * Uses each layout's width/length instead of a global sheet size
 */
const calculateStatsPerSheet = (layouts, pieces) => {
  let totalSheetArea = 0;
  let usedArea = 0;

  layouts.forEach(layout => {
    // Use the layout's own dimensions
    const layoutArea = layout.width * layout.length;
    totalSheetArea += layoutArea;
    usedArea += layout.pieces.reduce((sum, piece) =>
      sum + (piece.width * piece.length), 0);
  });

  const wastedArea = totalSheetArea - usedArea;
  const efficiency = totalSheetArea > 0 ? (usedArea / totalSheetArea * 100) : 0;

  const totalOriginalPieces = pieces.reduce((sum, piece) => sum + piece.quantity, 0);
  const placedCount = layouts.reduce((sum, layout) => sum + layout.pieces.length, 0);

  return {
    totalSheets: layouts.length,
    usedArea: usedArea.toFixed(2),
    wastedArea: wastedArea.toFixed(2),
    efficiency: efficiency.toFixed(1),
    unplacedPieces: totalOriginalPieces - placedCount,
    placedPieces: placedCount,
    totalPieces: totalOriginalPieces
  };
};

/**
 * Group pieces by thickness for separate optimizations (legacy)
 */
const groupPiecesByThickness = (pieces) => {
  const groups = {};
  const tolerance = 0.125; // 1/8" tolerance for grouping

  pieces.forEach(piece => {
    const thickness = piece.thickness || 0;

    // Find existing group within tolerance
    let foundGroup = null;
    for (const key of Object.keys(groups)) {
      if (Math.abs(parseFloat(key) - thickness) < tolerance) {
        foundGroup = key;
        break;
      }
    }

    if (foundGroup) {
      groups[foundGroup].push(piece);
    } else {
      groups[thickness.toString()] = [piece];
    }
  });

  // Return as array sorted by thickness
  return Object.entries(groups)
    .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
    .map(([thickness, groupPieces]) => ({
      thickness: parseFloat(thickness),
      pieces: groupPieces
    }));
};

/**
 * Group pieces by stock sheet type for separate optimizations
 * Pieces are grouped by their explicit assignment or auto-matched by thickness
 */
const groupPiecesByStockType = (pieces, stockSheetTypes, defaultSheetWidth, defaultSheetLength, thicknessTol = 0.125) => {
  const groups = {};
  const tolerance = thicknessTol;

  pieces.forEach(piece => {
    let stockType = null;

    // First check explicit assignment
    if (piece.stockSheetTypeId) {
      stockType = stockSheetTypes.find(t => t.id === piece.stockSheetTypeId);
    }

    // If no explicit assignment, auto-match by thickness
    if (!stockType) {
      stockType = stockSheetTypes.find(t =>
        Math.abs(t.thickness - (piece.thickness || 0)) < tolerance
      );
    }

    // Use stock type id as key, or 'unassigned' for pieces without a match
    const key = stockType ? stockType.id : 'unassigned';

    if (!groups[key]) {
      groups[key] = {
        stockType: stockType || {
          id: 'unassigned',
          name: 'Unassigned',
          width: defaultSheetWidth,
          length: defaultSheetLength,
          thickness: 0
        },
        pieces: []
      };
    }
    groups[key].pieces.push(piece);
  });

  // Return as array sorted by stock type name
  return Object.values(groups).sort((a, b) =>
    a.stockType.name.localeCompare(b.stockType.name)
  );
};

/**
 * Hook for cut list optimization
 */
export const useOptimizer = () => {
  const {
    pieces,
    sheetWidth,
    sheetLength,
    cutWidth,
    trimFactoryEdges,
    trimAmount,
    algorithm,
    setLayouts,
    setStats,
    addError,
    stockSheetTypes,
    getStockTypeForPiece,
    currentUnits
  } = useCutList();

  /**
   * Run optimization for a single set of pieces (one thickness group)
   * Automatically uses polygon packing for non-rectangular shapes
   * Falls back to rectangle packing if polygon packing produces invalid results
   * @param {Array} allPieces - Pieces to optimize
   * @param {string} algorithmType - Algorithm to use
   * @param {number} groupSheetWidth - Sheet width for this thickness group
   * @param {number} groupSheetHeight - Sheet height for this thickness group
   */
  const runSingleOptimization = useCallback(async (allPieces, algorithmType, groupSheetWidth, groupSheetHeight) => {
    let bestResult = null;

    // Check if we have non-rectangular pieces
    const hasPolygons = hasNonRectangularPieces(allPieces);

    // Use SVGnest for polygons OR if explicitly selected
    const autoSwitched = hasPolygons && algorithmType !== ALGORITHMS.SVGNEST;
    if (algorithmType === ALGORITHMS.SVGNEST || hasPolygons) {
      if (autoSwitched) {
        console.log('Optimization: Detected non-rectangular pieces, auto-switching to SVGnest algorithm');
      } else {
        console.log('Optimization: Using SVGnest algorithm');
      }

      try {
        bestResult = await svgnestOptimize(allPieces, groupSheetWidth, groupSheetHeight, cutWidth, {
          timeLimit: 8000, // 8 seconds for genetic algorithm
          rotationSteps: 4
        });

        // Only fall back if SVGnest completely failed (no layouts at all)
        // or if layouts are invalid (overlapping pieces)
        const hasLayouts = bestResult && bestResult.layouts && bestResult.layouts.length > 0;
        const isValid = hasLayouts && validateOptimizationResult(bestResult, groupSheetWidth, groupSheetHeight);

        if (!hasLayouts || !isValid) {
          const reason = !hasLayouts ? 'no layouts' : 'invalid overlapping layouts';
          console.warn(`Optimization: SVGnest returned ${reason}, trying polygon nesting fallback`);

          // First try our polygon nesting implementation
          bestResult = nestPolygons(allPieces, groupSheetWidth, groupSheetHeight, cutWidth, {
            rotations: [0, 90, 180, 270]
          });

          const polygonValid = bestResult && bestResult.layouts &&
                              bestResult.layouts.length > 0 &&
                              validateOptimizationResult(bestResult, groupSheetWidth, groupSheetHeight);

          if (!polygonValid) {
            // Last resort: rectangle bounding boxes
            console.warn('Optimization: Polygon nesting also failed, using rectangle fallback');
            bestResult = rectangleFallbackOptimize(allPieces, groupSheetWidth, groupSheetHeight, cutWidth, ALGORITHMS.MAXIMAL_RECTANGLES);
            if (bestResult) {
              bestResult.usedFallback = true;
            }
          }
        }
        // If SVGnest worked (even with some unplaced pieces), keep its result
        // Don't fall back just because of unplaced pieces - that's expected for large jobs

        // Mark if we auto-switched to SVGnest
        if (autoSwitched && bestResult) {
          bestResult.autoSwitchedToSvgnest = true;
        }

        return bestResult;
      } catch (error) {
        console.error('Optimization: SVGnest failed with error:', error);

        // Try polygon nesting before rectangle fallback
        try {
          bestResult = nestPolygons(allPieces, groupSheetWidth, groupSheetHeight, cutWidth, {
            rotations: [0, 90, 180, 270]
          });

          const polygonValid = bestResult && bestResult.layouts &&
                              bestResult.layouts.length > 0 &&
                              validateOptimizationResult(bestResult, groupSheetWidth, groupSheetHeight);

          if (polygonValid) {
            return bestResult;
          }
        } catch (polygonError) {
          console.error('Optimization: Polygon nesting also failed:', polygonError);
        }

        // Last resort: rectangle fallback
        bestResult = rectangleFallbackOptimize(allPieces, groupSheetWidth, groupSheetHeight, cutWidth, ALGORITHMS.MAXIMAL_RECTANGLES);
        if (bestResult) {
          bestResult.usedFallback = true;
        }
        return bestResult;
      }
    }

    // Run the selected algorithm for rectangles
    switch (algorithmType) {
      case ALGORITHMS.SKYLINE:
        bestResult = skylineOptimize(allPieces, groupSheetWidth, groupSheetHeight, cutWidth);
        break;

      case ALGORITHMS.FFDH:
        bestResult = ffdhOptimize(allPieces, groupSheetWidth, groupSheetHeight, cutWidth);
        break;

      case ALGORITHMS.MAXIMAL_RECTANGLES:
        bestResult = maximalRectanglesOptimize(allPieces, groupSheetWidth, groupSheetHeight, cutWidth);
        break;

      case ALGORITHMS.BEST_RESULT:
        bestResult = await bestResultOptimizeAsync(allPieces, groupSheetWidth, groupSheetHeight, cutWidth);
        break;

      case ALGORITHMS.BOTTOM_LEFT:
      default:
        // Use the original iterative approach for bottom-left
        const totalPieceArea = allPieces.reduce((sum, piece) =>
          sum + (piece.width * piece.height), 0);
        const sheetArea = groupSheetWidth * groupSheetHeight;
        const theoreticalMin = Math.ceil(totalPieceArea / sheetArea);

        let sheetsToTry = Math.max(theoreticalMin, 1);
        const maxSheets = allPieces.length;
        let lastUnplacedCount = allPieces.length;

        while (sheetsToTry <= maxSheets) {
          const result = tryOptimizeWithSheets(allPieces, sheetsToTry, groupSheetWidth, groupSheetHeight, cutWidth);

          if (result.unplacedCount === 0) {
            bestResult = result;
            break;
          }

          if (!bestResult || result.unplacedCount < bestResult.unplacedCount) {
            bestResult = result;
          }

          if (result.unplacedCount === lastUnplacedCount && sheetsToTry > theoreticalMin + 10) {
            break;
          }

          lastUnplacedCount = result.unplacedCount;
          sheetsToTry++;
        }
        break;
    }

    return bestResult;
  }, [cutWidth]);

  const optimize = useCallback(async () => {
    console.log('Optimization: Starting with', pieces.length, 'piece types');
    console.log('Optimization: Default sheet dimensions:', sheetWidth, 'x', sheetLength);
    console.log('Optimization: Stock sheet types:', stockSheetTypes.length);
    console.log('Optimization: Cut width:', cutWidth);
    console.log('Optimization: Algorithm:', algorithm);

    if (pieces.length === 0) {
      addError('No pieces to optimize');
      return null;
    }

    // Group pieces by stock sheet type
    const thickTol = getThicknessTolerance(currentUnits?.unit || 'inches', currentUnits?.system || 'imperial');
    const stockTypeGroups = groupPiecesByStockType(pieces, stockSheetTypes, sheetWidth, sheetLength, thickTol);
    console.log('Optimization: Found', stockTypeGroups.length, 'stock type group(s)');

    // Combined results across all groups
    let allLayouts = [];
    let totalUnplaced = 0;
    let layoutIdCounter = 1;
    let usedFallback = false;
    let autoSwitchedToSvgnest = false;

    // Calculate effective trim amount (0 if disabled)
    const effectiveTrim = trimFactoryEdges ? trimAmount : 0;

    // Optimize each stock type group separately
    for (const group of stockTypeGroups) {
      const stockType = group.stockType;
      const groupLabel = stockType.name;
      const groupSheetWidth = stockType.width;
      const groupSheetLength = stockType.length;

      // Calculate usable area after trimming factory edges
      const usableWidth = groupSheetWidth - (2 * effectiveTrim);
      const usableLength = groupSheetLength - (2 * effectiveTrim);

      console.log(`Optimization: Processing "${groupLabel}" with ${group.pieces.length} piece types, sheet ${groupSheetWidth.toFixed(1)}x${groupSheetLength.toFixed(1)}${effectiveTrim > 0 ? ` (usable: ${usableWidth.toFixed(1)}x${usableLength.toFixed(1)} after trim)` : ''}`);

      // Expand pieces by quantity for this group
      const expandedPieces = [];
      group.pieces.forEach(piece => {
        for (let i = 0; i < piece.quantity; i++) {
          expandedPieces.push({
            ...piece,
            id: `${piece.id}_${i}`,
            originalId: piece.id,
            pieceIndex: i
          });
        }
      });

      if (expandedPieces.length === 0) continue;

      // Check for oversized pieces against usable sheet size (after trim)
      const tooLargePieces = expandedPieces.filter(p =>
        (p.width > usableWidth && p.width > usableLength) ||
        (p.length > usableLength && p.length > usableWidth)
      );
      if (tooLargePieces.length > 0) {
        const sizeLabel = effectiveTrim > 0
          ? `usable area (${usableWidth.toFixed(1)}x${usableLength.toFixed(1)} after trim)`
          : `sheet size (${groupSheetWidth.toFixed(1)}x${groupSheetLength.toFixed(1)})`;
        addError(`${tooLargePieces.length} piece(s) in "${groupLabel}" are larger than the ${sizeLabel}`);
      }

      // Run optimization with usable dimensions (after trim)
      // Map pieces to algorithm space: app length→algo width (x), app width→algo height (y)
      const algoPieces = expandedPieces.map(toAlgoSpace);
      // Map sheet dims: app length→algo sheetWidth (x), app width→algo sheetHeight (y)
      const result = await runSingleOptimization(algoPieces, algorithm, usableLength, usableWidth);

      if (result) {
        // Track if fallback was used
        if (result.usedFallback) {
          usedFallback = true;
        }
        // Track if auto-switched to SVGnest
        if (result.autoSwitchedToSvgnest) {
          autoSwitchedToSvgnest = true;
        }

        // Add stock type info, sheet dimensions, and renumber layouts
        // Offset piece positions by trim amount so they render inside the trimmed area
        const groupLayouts = result.layouts.map(layout => ({
          ...layout,
          id: layoutIdCounter++,
          stockType: stockType,
          stockTypeId: stockType.id,
          stockTypeName: groupLabel,
          thickness: stockType.thickness,
          thicknessLabel: formatLayoutLabel(
            { stockType, thickness: stockType.thickness, width: groupSheetWidth, length: groupSheetLength },
            currentUnits?.unit || 'in',
            currentUnits?.system || UNIT_SYSTEMS.IMPERIAL
          ),
          // Use full sheet dimensions for display
          width: groupSheetWidth,
          length: groupSheetLength,
          // Store trim info for visualization
          trimAmount: effectiveTrim,
          // Offset all piece positions by trim amount
          pieces: layout.pieces.map(piece => ({
            ...fromAlgoSpace(piece),
            x: piece.x + effectiveTrim,
            y: piece.y + effectiveTrim
          }))
        }));

        allLayouts = [...allLayouts, ...groupLayouts];
        totalUnplaced += result.unplacedCount;

        console.log(`Optimization: "${groupLabel}" - ${groupLayouts.length} sheets, ${result.unplacedCount} unplaced`);
      }
    }

    if (allLayouts.length > 0) {
      console.log('Optimization: Complete. Total sheets needed:', allLayouts.length);
      setLayouts(allLayouts);
      // Calculate stats using per-layout sheet sizes
      const stats = calculateStatsPerSheet(allLayouts, pieces);
      setStats(stats);

      if (autoSwitchedToSvgnest && !usedFallback) {
        addError('Note: Detected non-rectangular shapes. Automatically using SVGnest polygon packing algorithm.');
      }

      if (usedFallback) {
        addError('Note: Polygon packing failed. Shapes were packed as bounding rectangles. Results may use more material than optimal.');
      }

      if (totalUnplaced > 0) {
        addError(`Warning: ${totalUnplaced} pieces could not be placed`);
      }

      return { layouts: allLayouts, unplacedCount: totalUnplaced, usedFallback };
    } else {
      console.error('Optimization: Failed to find any solution');
      addError('Optimization failed: Could not find a valid layout');
      return null;
    }
  }, [pieces, sheetWidth, sheetLength, cutWidth, trimFactoryEdges, trimAmount, algorithm, runSingleOptimization, setLayouts, setStats, addError, stockSheetTypes]);

  return { optimize };
};

export default useOptimizer;
